import { Autocomplete, Box, Button, DialogContent, Stack, TextField } from '@mui/material';
import React, { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { createNewFeeArrangementLevel, replaceFeeArrangementLevel } from 'wisestep-api';
import {
  ControlledTextInput,
  WsButton,
  WsDialog,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { useNotificationStore } from '@/utils/NotificationStore';

import { failed, success, warning } from '../../../utils/NotificationMessages';
import { HeaderBox } from '../../jobSettings/components/HeaderBox';
import { getClients } from '../helper/ClientSettingsHelper';

interface IAddEditArrangementLevelDialog {
  isOpen: boolean;
  defaultValues: any;
  onCancel(): void;
  onSave(updatedArrangementLevel): void;
}

const AddEditArrangementLevelDialog = ({
  isOpen,
  defaultValues,
  onCancel,
  onSave,
}: IAddEditArrangementLevelDialog) => {
  const methods = useForm({
    defaultValues: defaultValues,
  });
  const [clientOptions, setClientOptions] = useState(
    defaultValues.client ? [defaultValues.client] : []
  );
  const setNotification = useNotificationStore((state) => state.setNotification);

  const handleSave = async (data) => {
    try {
      let updatedArrangementLevel;
      if (data.id) {
        updatedArrangementLevel = (await replaceFeeArrangementLevel(data)).data;
        setNotification(success(`${data.name} has been added successfully !!`));
      } else {
        updatedArrangementLevel = (
          await createNewFeeArrangementLevel(data.client.id as string, data.name)
        ).data;
        setNotification(success(`${data.name} has been added successfully !!`));
      }

      onSave(updatedArrangementLevel);
    } catch (error: any) {
      if (error.response.status === 400)
        setNotification(warning(`Oops , ${data.name} already exists!!`));
      else setNotification(failed('Something went wrong !!'));
    }
  };

  return (
    <FormProvider {...methods}>
      <WsDialog openDialog={isOpen} size="small">
        <DialogContent>
          <HeaderBox>
            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
              Add/Edit Arrangement Level
            </WsTypography>
          </HeaderBox>
          <form onSubmit={methods.handleSubmit(handleSave)}>
            <Stack mt={2} direction="row" spacing={2}>
              <Box sx={{ flexGrow: 1 }}>
                <Controller
                  control={methods.control}
                  name="client"
                  render={({ field: { onChange } }) => (
                    <Autocomplete
                      disabled={defaultValues.client?.id || defaultValues.client?.value}
                      defaultValue={defaultValues.client}
                      size="small"
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                      options={clientOptions}
                      getOptionLabel={(option: any) => {
                        if (typeof option === 'string') {
                          return option;
                        }
                        if (option.inputValue) {
                          return option.inputValue;
                        }
                        return option.name;
                      }}
                      onChange={(event, item) => {
                        onChange(item);
                      }}
                      onInputChange={(event, value) => {
                        getClients(value).then(setClientOptions);
                      }}
                      renderInput={(params) => <TextField {...params} label="Search client..." />}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <ControlledTextInput name="name" placeholder="Arrangement Level" size="small" />
              </Box>
            </Stack>
            <Stack mt={4} direction="row" justifyContent="flex-end" spacing={2}>
              <Button color="error" onClick={onCancel}>
                Cancel
              </Button>
              <WsButton type="submit">Save</WsButton>
            </Stack>
          </form>
        </DialogContent>
      </WsDialog>
    </FormProvider>
  );
};

export default AddEditArrangementLevelDialog;
